import React, { useState, useMemo } from 'react';
import Sparkline from './Sparkline';
import { activationColor } from './bonusStatus';
import { Spinner } from '../ui/spinner';


function fmtEur(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1000000) return '€' + (n / 1000000).toFixed(2) + 'M';
  if (abs >= 1000) return '€' + (n / 1000).toFixed(1) + 'K';
  return '€' + Math.round(n).toLocaleString('en-GB');
}

const DIR_STYLE = {
  up:   { icon: '↑', color: '#00c896' },
  down: { icon: '↓', color: '#e05555' },
  flat: { icon: '–', color: '#7a8499' },
};

function ratioDir(r) {
  if (r > 1.1) return 'up';
  if (r < 0.9) return 'down';
  return 'flat';
}

function buildColumns(days) {
  return [
    { key: 'user_id',                     label: 'Player',                   align: 'left',  sortable: true },
    { key: 'total_stake_eur',             label: 'Total turnover',           align: 'right', sortable: true,  fmt: fmtEur },
    { key: 'avg_stake_per_day',           label: 'Avg / day',                align: 'right', sortable: true,  fmt: fmtEur },
    { key: 'spark',                       label: `Turnover ${days}d`,        align: 'left',  sortable: false },
    { key: 'recent_vs_prior_stake_ratio', label: 'Recent÷prior',             align: 'right', sortable: true },
    { key: 'total_deposits_eur',           label: 'Deposits',                 align: 'right', sortable: true,  fmt: fmtEur },
    { key: 'total_bonus_value_eur',       label: 'Bonus value',              align: 'right', sortable: true,  fmt: fmtEur },
    { key: 'bonus_to_deposit_ratio',      label: 'Bonus÷deposits',           align: 'right', sortable: true },
    { key: 'activation_rate',             label: 'Activation',               align: 'right', sortable: true },
    { key: 'wager_done_rate',             label: 'Wager done',               align: 'right', sortable: true },
    { key: 'canceled_rate',              label: 'Canceled',                 align: 'right', sortable: true },
  ];
}

const GRID_COLS = '180px 1fr 1fr 100px 1fr 1fr 1fr 1fr 0.9fr 0.9fr 0.9fr';

const QUADRANTS = [
  { key: 'retention_risk',    label: 'Retention risk',      color: '#e05555', x: 'down', y: 'high' },
  { key: 'declining_low',     label: 'Declining · low bonus', color: '#e0a23c', x: 'down', y: 'low'  },
  { key: 'bonus_growth',      label: 'Bonus-driven growth', color: '#e0a23c', x: 'up',   y: 'high' },
  { key: 'healthy',           label: 'Healthy',             color: '#00c896', x: 'up',   y: 'low'  },
];

function quadrantOf(p) {
  const growing = (p.recent_vs_prior_stake_ratio || 0) >= 1;
  const bonusHeavy = (p.bonus_to_deposit_ratio || 0) > 0.5;
  if (!growing && bonusHeavy)  return 'retention_risk';
  if (!growing && !bonusHeavy) return 'declining_low';
  if (growing  && bonusHeavy)  return 'bonus_growth';
  return 'healthy';
}

export default function TrajectoryBookList({ players, loading, selectedUserId, onSelect, days = 180, quadrantFilter, onQuadrantFilterChange }) {
  const [sortKey, setSortKey] = useState('total_stake_eur');
  const setQuadrantFilter = onQuadrantFilterChange;
  const [sortDir, setSortDir] = useState('desc');

  const counts = useMemo(() => {
    const c = { retention_risk: 0, declining_low: 0, bonus_growth: 0, healthy: 0 };
    for (const p of players || []) c[quadrantOf(p)]++;
    return c;
  }, [players]);

  const sorted = useMemo(() => {
    const rows = (players || []).filter(p => !quadrantFilter || quadrantOf(p) === quadrantFilter);
    rows.sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [players, sortKey, sortDir, quadrantFilter]);

  const columns = buildColumns(days);
  const recentDays = Math.round(days * 28 / 180);
  const priorDays = Math.round(days * 152 / 180);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <div className="bg-[#13161b] border border-[#2a3042] rounded overflow-hidden">
      <div className="px-4 pt-3 pb-2 border-b border-[#1f2430]">
        {/* Title row */}
        <div className="flex items-center justify-between gap-4 mb-3">
          <p className="text-sm font-bold text-[#d8dde8]">Player book</p>
          <p className="text-[10px] text-[#3d4558]">Sortable on every column · click row to open player</p>
        </div>

        {/* Quadrant filters */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] text-[#3d4558] shrink-0">Triage</span>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setQuadrantFilter(null)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium transition-colors"
              style={{
                background: !quadrantFilter ? '#2a3042' : 'transparent',
                border: `1px solid ${!quadrantFilter ? '#4a5570' : '#252b3b'}`,
                color: !quadrantFilter ? '#d8dde8' : '#5a6477',
                cursor: 'pointer',
              }}
            >
              All <span style={{ color: !quadrantFilter ? '#7a8499' : '#3d4558', marginLeft: 2 }}>{(players || []).length}</span>
            </button>
            {QUADRANTS.map((q) => {
              const active = quadrantFilter === q.key;
              return (
                <button
                  key={q.key}
                  onClick={() => setQuadrantFilter(active ? null : q.key)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium transition-colors"
                  style={{
                    background: active ? `${q.color}18` : 'transparent',
                    border: `1px solid ${active ? q.color : '#252b3b'}`,
                    color: active ? q.color : '#5a6477',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: q.color, display: 'inline-block', opacity: active ? 1 : 0.35 }} />
                  {q.label}
                  <span style={{ color: active ? `${q.color}aa` : '#3d4558', marginLeft: 1 }}>{counts[q.key]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Column definitions */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#3d4558] shrink-0">Legend</span>
          <div className="flex flex-wrap gap-1.5">
          {[
            { label: 'Sparkline',      value: `daily turnover, ${days}d window` },
            { label: 'Recent÷prior',   value: `last ${recentDays}d vs first ${priorDays}d avg` },
            { label: 'Bonus value',    value: 'total issued' },
            { label: 'Bonus÷deposits', value: '>50% = amber' },
            { label: 'Wager done',     value: 'finished ÷ activated' },
            { label: 'Canceled',       value: 'canceled ÷ issued' },
          ].map(({ label, value }) => (
            <span
              key={label}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
              style={{ background: 'transparent', borderBottom: '1px solid #1f2430' }}
            >
              <span style={{ color: '#5a6477', fontWeight: 600 }}>{label}</span>
              <span style={{ color: '#4a5268' }}>{value}</span>
            </span>
          ))}
          </div>
        </div>
      </div>

      {loading && sorted.length === 0 ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : !loading && sorted.length === 0 ? (
        <div className="py-10 text-center">
          <p className="text-xs text-ink-dim">No players for this agent.</p>
        </div>
      ) : (
        <div className="overflow-x-auto" style={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.15s' }}>
          <div style={{ minWidth: 960 }}>
            {/* Header row */}
            <div
              className="grid gap-2 px-4 py-1.5 border-b border-[#1f2430] sticky top-0 bg-[#13161b]"
              style={{ gridTemplateColumns: GRID_COLS }}
            >
              {columns.map((c) => (
                <span
                  key={c.key}
                  onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                  className="text-[10px] font-bold uppercase tracking-[0.4px] select-none"
                  style={{
                    textAlign: c.align,
                    cursor: c.sortable ? 'pointer' : 'default',
                    color: sortKey === c.key ? '#d4a843' : '#5a6477',
                  }}
                >
                  {c.label}{sortKey === c.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                </span>
              ))}
            </div>

            {/* Body rows */}
            <div className="max-h-[420px] overflow-y-auto">
              {sorted.map((p) => {
                const selected = p.user_id === selectedUserId;
                const dir = ratioDir(p.recent_vs_prior_stake_ratio);
                const ds = DIR_STYLE[dir];
                return (
                  <div
                    key={p.user_id}
                    onClick={() => onSelect(p.user_id)}
                    className="grid gap-2 px-4 py-1.5 items-center cursor-pointer border-b border-[#1f2430] border-l-[3px] transition-colors"
                    style={{
                      gridTemplateColumns: GRID_COLS,
                      borderLeftColor: selected ? '#d4a843' : 'transparent',
                      backgroundColor: selected ? 'rgba(212,168,67,0.071)' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!selected) e.currentTarget.style.backgroundColor = '#1f2430';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = selected ? 'rgba(212,168,67,0.071)' : 'transparent';
                    }}
                  >
                    {/* Player name + id */}
                    <div className="min-w-0">
                      <p className="text-xs text-[#d8dde8] font-semibold truncate">
                        {p.email || `User ${p.user_id}`}
                      </p>
                      <p className="text-[10px] text-ink-dim">ID {p.user_id}</p>
                    </div>

                    {/* Total turnover */}
                    <span className="text-xs text-[#d8dde8] text-right">{fmtEur(p.total_stake_eur)}</span>

                    {/* Avg / day */}
                    <span className="text-xs text-[#d8dde8] text-right">{fmtEur(p.avg_stake_per_day)}</span>

                    {/* Sparkline */}
                    <Sparkline values={p.stake_sparkline} />

                    {/* Recent÷prior */}
                    <span className="flex items-baseline justify-end gap-1">
                      <span className="text-xs font-bold" style={{ color: ds.color }}>{ds.icon}</span>
                      <span className="text-xs text-[#d8dde8]">
                        {p.recent_vs_prior_stake_ratio != null ? p.recent_vs_prior_stake_ratio.toFixed(2) + '×' : '—'}
                      </span>
                    </span>

                    {/* Deposits */}
                    <span className="text-xs text-[#d8dde8] text-right">{fmtEur(p.total_deposits_eur)}</span>

                    {/* Bonus value */}
                    <span className="text-xs text-[#d8dde8] text-right">{fmtEur(p.total_bonus_value_eur)}</span>

                    {/* Bonus÷deposits */}
                    <span
                      className="text-xs text-right"
                      style={{ color: p.bonus_to_deposit_ratio > 0.5 ? '#e0a23c' : '#d8dde8' }}
                    >
                      {p.total_deposits_eur > 0 ? (p.bonus_to_deposit_ratio * 100).toFixed(1) + '%' : '—'}
                    </span>

                    {/* Activation */}
                    <span
                      className="text-xs text-right"
                      style={{ color: p.total_bonuses > 0 ? activationColor(p.activation_rate) : '#5a6477' }}
                    >
                      {p.total_bonuses > 0 ? Math.round(p.activation_rate * 100) + '%' : '—'}
                    </span>

                    {/* Wager done */}
                    <span
                      className="text-xs text-right"
                      style={{ color: p.total_bonuses > 0 ? activationColor(p.wager_done_rate) : '#5a6477' }}
                    >
                      {p.total_bonuses > 0 ? Math.round(p.wager_done_rate * 100) + '%' : '—'}
                    </span>

                    {/* Canceled */}
                    <span
                      className="text-xs text-right"
                      style={{ color: p.canceled_rate > 0.1 ? '#a85d5d' : p.total_bonuses > 0 ? '#d8dde8' : '#5a6477' }}
                    >
                      {p.total_bonuses > 0 ? Math.round(p.canceled_rate * 100) + '%' : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
